import axios from 'axios'

// Базовый URL для внешнего API видеоконференций
const EXTERNAL_VIDEO_API_BASE_URL = process.env.VUE_APP_EXTERNAL_VIDEO_API_BASE_URL || 'https://main.emd.kz/api/v1'

// Обычный API клиент с аутентификацией
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// Клиент для внешних API (без withCredentials и других спец. заголовков)
const externalApiClient = axios.create({
  timeout: 30000,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// Добавляем перехватчик для обработки ошибок
apiClient.interceptors.response.use(
  response => response,
  error => {
    return Promise.reject(error)
  }
)

// Перехватчик для добавления CSRF токена
apiClient.interceptors.request.use(config => {
  // Get CSRF token from cookie if it exists
  const csrfToken = getCookie('csrftoken')
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }

  // Если данные это FormData, удаляем Content-Type чтобы браузер установил правильные заголовки
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }


  return config
})

// Helper function to get cookies
function getCookie(name) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

// Отдельный API клиент для публичных эндпоинтов (без аутентификации)
const publicApiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// Добавляем перехватчик для обработки ошибок для публичного API
publicApiClient.interceptors.response.use(
  response => response,
  error => {
    return Promise.reject(error)
  }
)

function buildExternalVideoFileUrl(fileUrl) {
  if (!fileUrl || /^https?:\/\//i.test(fileUrl)) {
    return fileUrl
  }

  try {
    return new URL(fileUrl, EXTERNAL_VIDEO_API_BASE_URL).toString()
  } catch (error) {
    return fileUrl
  }
}

export default {
  // Базовые HTTP методы
  get(url, config) {
    return apiClient.get(url, config)
  },
  post(url, data, config) {
    return apiClient.post(url, data, config)
  },
  put(url, data, config) {
    return apiClient.put(url, data, config)
  },
  patch(url, data, config) {
    return apiClient.patch(url, data, config)
  },
  delete(url, config) {
    return apiClient.delete(url, config)
  },

  // Authentication endpoints
  checkAuth() {
    return apiClient.get('/users/check_auth/')
  },
  login(email, password, rememberMe = false) {
    return apiClient.post('/login/', { email, password, remember_me: rememberMe })
  },
  logout() {
    return apiClient.post('/logout/')
  },

  // Login history
  getLoginHistory() {
    return apiClient.get('/auth/login-history/')
  },

  // EDS (ЭЦП) Authentication endpoints
  getEDSChallenge() {
    return apiClient.post('/auth/eds/challenge/')
  },
  verifyEDS(signature) {
    return apiClient.post('/auth/eds/verify/', { signature })
  },
  getEDSStatus() {
    return apiClient.get('/auth/eds/status/')
  },

  // Password reset endpoints
  requestPasswordReset(email) {
    return publicApiClient.post('/auth/password-reset/request/', { email })
  },
  confirmPasswordReset(token, newPassword, confirmPassword) {
    return publicApiClient.post('/auth/password-reset/confirm/', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword
    })
  },

  // Registration endpoints
  registerPatient(userData) {
    return apiClient.post('/register/patient/', userData)
  },
  registerDoctor(userData) {
    return apiClient.post('/register/doctor/', userData)
  },
  registerNurse(userData) {
    return apiClient.post('/register/nurse/', userData)
  },

  // Doctor endpoints
  getDoctorProfile() {
    return apiClient.get('/doctors/me/')
  },
  updateDoctorProfile(profileData) {
    return apiClient.patch('/doctors/me/', profileData)
  },
  changePassword(passwordData) {
    return apiClient.put('/users/change_password/', passwordData)
  },
  hasSchedules() {
    return apiClient.get('/doctors/has_schedules/')
  },
  getDoctorMISSchedule(params = {}) {
    return apiClient.get('/doctors/mis-schedule/', {
      params: {
        _ts: Date.now(),
        ...params
      }
    })
  },
  getDoctorClinicPatients(params = {}) {
    return apiClient.get('/doctors/clinic-patients/', { params })
  },
  getAmcUltrasoundCapabilities() {
    return apiClient.get('/ultrasound/amc/capabilities/')
  },
  createAmcUltrasoundReport(data) {
    return apiClient.post('/ultrasound/amc/reports/', data, { timeout: 45000 })
  },
  listAmcUltrasoundReports(patientId, offset = 0, withoutStudy = false) {
    return apiClient.get('/ultrasound/amc/reports/', { params: { patient_id: patientId, offset, without_study: withoutStudy ? '1' : undefined } })
  },
  listUltrasoundStudies(offset = 0) {
    return apiClient.get('/ultrasound/amc/studies/', { params: { offset } })
  },
  getUltrasoundStudy(id) {
    return apiClient.get(`/ultrasound/amc/studies/${id}/`)
  },
  saveUltrasoundStudy(id, data) {
    return apiClient.post(`/ultrasound/amc/studies/${id}/`, data)
  },
  getAmcUltrasoundReport(id) {
    return apiClient.get(`/ultrasound/amc/reports/${id}/`)
  },
  getUltrasoundReportInput(id) {
    return apiClient.get(`/ultrasound/amc/reports/${id}/input/`)
  },
  resumeAmcUltrasoundReport(id) {
    return apiClient.post(`/ultrasound/amc/reports/${id}/resume/`, {}, { timeout: 45000 })
  },
  downloadAmcUltrasoundDocument(id) {
    return apiClient.get(`/ultrasound/amc/reports/${id}/document/`, { responseType: 'blob', timeout: 45000 })
  },
  getPublicIntegrationClinics() {
    return apiClient.get('/public/clinics/')
  },

  // Nurse endpoints (same as doctor endpoints but for nurses)
  getNurseProfile() {
    return apiClient.get('/nurses/me/')
  },
  updateNurseProfile(profileData) {
    return apiClient.patch('/nurses/me/', profileData)
  },
  getNurseClinicPatients(params = {}) {
    return apiClient.get('/nurses/clinic-patients/', { params })
  },
  createNurseVideoCall(patientId) {
    return apiClient.post('/nurses/create-video-call/', { patient_id: patientId })
  },
  getNurseVideoCallsHistory() {
    return apiClient.get('/nurses/video-calls-history/')
  },
  getNurseVideoCallRecords(callId) {
    return apiClient.get(`/nurses/${callId}/video-call-records/`)
  },

  // Пациент: история звонков медсестер (отдельно от приемов)
  getPatientNurseCallsHistory(params = {}) {
    return apiClient.get('/patients/nurse-calls-history/', { params })
  },

  // Пациент: записи конкретного звонка медсестры
  getPatientNurseCallRecords(callId) {
    return apiClient.get('/patients/nurse-call-records/', { params: { call_id: callId } })
  },

  // Patient video calls from nurses
  getPatientActiveVideoCalls() {
    return apiClient.get('/patients/active-video-calls/')
  },

  // Patient endpoints
  getPatientProfile() {
    return apiClient.get('/patients/me/')
  },
  getPatientById(patientId) {
    return apiClient.get(`/patients/${patientId}/`)
  },
  getDoctorPatientMedicalDocuments(patientId, params = {}) {
    return apiClient.get(`/patients/${patientId}/medical-documents/`, { params })
  },
  downloadDoctorPatientMedicalDocument(patientId, documentId) {
    return apiClient.get(`/patients/${patientId}/medical-documents/${documentId}/download/`, {
      responseType: 'blob'
    })
  },
  getPatientHealthTracker(patientId) {
    return apiClient.get(`/health-trackers/`, {
      params: {
        patient: patientId
      }
    })
  },
  getPatientClinicDoctors() {
    return apiClient.get('/patients/clinic-doctors/')
  },
  get24SevenDoctors(params = {}) {
    return apiClient.get('/patient/doctors/24-7/', { params })
  },

  // Doctor schedules
  getDoctorSchedules(params = {}) {
    return apiClient.get('/doctor-schedules/', { params })
  },
  createDoctorSchedule(scheduleData) {
    return apiClient.post('/doctor-schedules/', scheduleData)
  },
  updateDoctorSchedule(scheduleId, scheduleData) {
    return apiClient.put(`/doctor-schedules/${scheduleId}/`, scheduleData)
  },
  deleteDoctorSchedule(scheduleId) {
    return apiClient.delete(`/doctor-schedules/${scheduleId}/`)
  },
  getDoctorScheduleOverrides(params = {}) {
    return apiClient.get('/doctor-schedule-overrides/', { params })
  },
  createDoctorScheduleOverride(overrideData) {
    return apiClient.post('/doctor-schedule-overrides/', overrideData)
  },
  updateDoctorScheduleOverride(overrideId, overrideData) {
    return apiClient.put(`/doctor-schedule-overrides/${overrideId}/`, overrideData)
  },
  deleteDoctorScheduleOverride(overrideId) {
    return apiClient.delete(`/doctor-schedule-overrides/${overrideId}/`)
  },
  generateDoctorSlots(doctorId) {
    return apiClient.post(`/doctors/${doctorId}/generate_slots/`)
  },

  // Location data endpoints (публичные)
  getCities() {
    return publicApiClient.get('/cities/')
  },
  getClinics(cityId, includeAll = false) {
    let url = `/clinics/?city=${cityId}`
    if (includeAll) {
      url += '&include_all=true'
    }
    return publicApiClient.get(url)
  },

  // Appointments endpoints
  getMyAppointments(params = {}) {
    return apiClient.get('/patient/appointments/my_appointments/', { params })
  },
  getAllDoctorAppointments() {
    return apiClient.get('/patient/appointments/my_appointments/')
  },
  getUpcomingAppointments() {
    return apiClient.get('/patient/appointments/upcoming/')
  },
  getPastAppointments() {
    return apiClient.get('/patient/appointments/past/')
  },
  getTodayAppointments() {
    return apiClient.get('/patient/appointments/today/')
  },
  getDoctorAppointments(params = {}) {
    // Handle special status filters with dedicated endpoints
    if (params.status === 'upcoming') {
      return this.getUpcomingAppointments();
    } else if (params.status === 'today') {
      return this.getTodayAppointments();
    } else if (params.status === 'past') {
      return this.getPastAppointments();
    } else {
      return apiClient.get('/patient/appointments/', { params })
    }
  },
  getPatientAppointments(filters = {}) {
    // Use the same endpoints as doctor but for the patient context
    if (filters.status === 'upcoming') {
      return this.getUpcomingAppointments();
    } else if (filters.status === 'past') {
      return this.getPastAppointments();
    } else if (filters.status === 'today') {
      return this.getTodayAppointments();
    } else {
      return this.getMyAppointments();
    }
  },
  getDoctorById(doctorId) {
    return apiClient.get(`/doctors/${doctorId}/`);
  },
  getNurseById(nurseId) {
    return apiClient.get(`/nurses/${nurseId}/`);
  },
  getPatientAppointmentsHistory(patientId, params = {}) {
    return apiClient.get(`/patient/appointments/patient/${patientId}/history/`, { params })
  },
  createAppointment(data) {
    return apiClient.post('/patient/appointments/', data)
  },
  getAppointmentPayment(appointmentId) {
    return apiClient.get(`/patient/appointments/${appointmentId}/payment/`)
  },
  createKaspiQuickPay(appointmentId, payload = {}) {
    return apiClient.post(`/patient/appointments/${appointmentId}/kaspi-quick-pay/`, payload)
  },
  cancelAppointment(appointmentId) {
    return apiClient.post(`/patient/appointments/${appointmentId}/cancel/`)
  },
  updateAppointmentStatus(appointmentId, status) {
    return apiClient.patch(`/patient/appointments/${appointmentId}/`, { status })
  },
  getAppointmentTranscript(appointmentId) {
    return apiClient.get(`/patient/appointments/${appointmentId}/transcript/`)
  },

  // Available slots endpoints
  getAvailableSlots(doctorId, startDate, endDate) {
    const params = {};
    if (doctorId) params.doctor = doctorId;
    if (startDate) params.date_from = startDate;
    if (endDate) params.date_to = endDate;

    return apiClient.get('/patient/available-slots/', { params })
  },
  getSlotsByIds(slotIds) {
    return apiClient.get('/patient/available-slots/', {
      params: { ids: slotIds.join(',') }
    })
  },
  getSlotById(slotId) {
    return apiClient.get(`/patient/available-slots/${slotId}/`)
  },
  getDoctorAvailableSlots(doctorId) {
    return apiClient.get(`/patient/available-slots/by_doctor_schedule/`, {
      params: { doctor: doctorId }
    })
  },
  getDoctorSlotsByDate(doctorId, date) {
    return apiClient.get(`/patient/available-slots/by_doctor_date/`, {
      params: { doctor: doctorId, date: date }
    })
  },

  // Health metrics endpoints
  getHealthMetrics(filters = {}) {
    const params = { ...filters }
    return apiClient.get('/health-trackers/list_records/', { params })
      .then(response => {
        // Handle empty response or error cases
        if (!response.data) {
          return { data: [], stats: {} };
        }

        // Extract stats and results from the response
        const stats = response.data.stats || {};

        // Filter out null/undefined values and ensure it's an array
        const results = Array.isArray(response.data.results)
          ? response.data.results.filter(item => item)
          : [];

        return { data: results, stats };
      })
      .catch(() => {
        // Return empty array instead of propagating the error
        return { data: [], stats: {} };
      });
  },
  createHealthMetric(healthData) {
    return apiClient.post('/health-trackers/', healthData)
  },
  updateHealthMetric(metricId, healthData) {
    return apiClient.put(`/health-trackers/${metricId}/`, healthData)
  },
  deleteHealthMetric(metricId) {
    return apiClient.delete(`/health-trackers/${metricId}/`)
  },

  // Video consultation endpoints
  createVideoRoom() {
    // Generate a cryptographically secure unique room code
    // Using crypto.getRandomValues for high entropy PRNG instead of Math.random()
    const array = new Uint8Array(6);
    crypto.getRandomValues(array);
    const roomCode = Array.from(array, byte => byte.toString(36)).join('').substring(0, 8);


    return externalApiClient.post(`${EXTERNAL_VIDEO_API_BASE_URL}/rooms/create`, { code: roomCode })
      .then(response => {
        return response;
      })
      .catch(() => {

        // Don't use fallback data since records won't work without real API response
        throw new Error('Не удалось создать видеокомнату. Сервис видеосвязи временно недоступен.');
      });
  },

  // Update appointment with video room URL
  updateAppointmentVideoRoom(appointmentId, videoRoomData) {
    const payload = {};

    // Include status if provided
    if (videoRoomData.status) {
      payload.status = videoRoomData.status;
    }

    // Only include properties that exist in the videoRoomData with correct field names
    if (videoRoomData.video_room_url || videoRoomData.url) {
      payload.video_room_url = videoRoomData.video_room_url || videoRoomData.url;
    }

    if (videoRoomData.video_room_name || videoRoomData.room_name) {
      payload.video_room_name = videoRoomData.video_room_name || videoRoomData.room_name;
    }

    // Log the payload for debugging

    // Don't make the API call if we have no data to update
    if (Object.keys(payload).length === 0) {
      return Promise.resolve({ data: null });
    }

    return apiClient.patch(`/appointments/${appointmentId}/`, payload);
  },

  // Get video room records
  getRoomRecords(roomName) {

    return externalApiClient.get(`${EXTERNAL_VIDEO_API_BASE_URL}/rooms/${roomName}/records`)
      .then(response => {
        return response;
      })
      .catch(() => {

        // Return empty records if API fails
        return {
          data: {
            result: 'success',
            data: {
              record_urls: []
            }
          }
        };
      });
  },

  // Get video room chat messages
  getRoomChat(roomName) {

    return externalApiClient.get(`${EXTERNAL_VIDEO_API_BASE_URL}/chat/${roomName}/history`)
      .then(response => {
        const rawMessages = Array.isArray(response?.data?.messages)
          ? response.data.messages
          : Array.isArray(response?.data?.data?.messages)
            ? response.data.data.messages
            : [];

        const messages = rawMessages
          .map(message => {
            const text = message?.text ?? message?.message ?? '';
            const hasAttachment = Boolean(
              message?.file_url || message?.file_name || message?.file_id
            );

            if (!text && !hasAttachment) {
              return null;
            }

            return {
              ...message,
              text,
              file_url: buildExternalVideoFileUrl(message?.file_url),
            };
          })
          .filter(Boolean);

        return {
          ...response,
          data: {
            ...response.data,
            room_name: response?.data?.room_name || response?.data?.data?.room_name || roomName,
            messages,
            messages_count: messages.length,
          }
        };
      })
      .catch(() => {

        // Return mock chat data if API fails, using the correct structure from API docs
        return {
          data: {
            messages: [
              {
                id: "1",
                sender_id: "doctor_1",
                sender_name: "Врач",
                message: "Добро пожаловать на консультацию",
                message_type: "text",
                room_name: roomName,
                created_at: new Date().toISOString(),
                timestamp: Math.floor(Date.now() / 1000)
              },
              {
                id: "2",
                sender_id: "patient_1",
                sender_name: "Пациент",
                message: "Спасибо, доктор",
                message_type: "text",
                room_name: roomName,
                created_at: new Date().toISOString(),
                timestamp: Math.floor(Date.now() / 1000)
              }
            ],
            room_name: roomName
          }
        };
      });
  },

  // Language endpoints
  setLanguage(languageCode) {
    return apiClient.post('/language/set/', { language: languageCode })
  },
  getLanguages() {
    return apiClient.get('/language/list/')
  },

  // Appointments API
  getBookingServices(params = {}) {
    return apiClient.get('/patient/appointments/booking-services/', { params })
  },
  getVisitReasons(params = {}) {
    return apiClient.get('/patient/appointments/visit_reasons/', { params: { ...params, purpose: 'reason' } })
  },

  // Appointment conclusion API
  saveAppointmentConclusion(conclusionData) {
    return apiClient.post('/appointment-conclusions/conclusion/', conclusionData)
  },
  prepareAppointmentConclusionSign(conclusionData) {
    return apiClient.post('/appointment-conclusions/prepare-sign/', conclusionData)
  },
  finalizeAppointmentConclusionSign({ appointmentId, signingContextId, cmsSignature }) {
    return apiClient.post('/appointment-conclusions/finalize-sign/', {
      appointment_id: appointmentId,
      signing_context_id: signingContextId,
      cms_signature: cmsSignature
    })
  },
  getAppointmentConclusionSigningPolicy(appointmentId) {
    return apiClient.get('/appointment-conclusions/signing-policy/', {
      params: appointmentId ? { appointment_id: appointmentId } : {}
    })
  },
  getAppointmentConclusionRevisions(appointmentId) {
    return apiClient.get(`/appointment-conclusions/${appointmentId}/revisions/`)
  },
  getAppointmentConclusionSignedPdfUrl(appointmentId) {
    return `${apiClient.defaults.baseURL}/appointment-conclusions/${appointmentId}/signed-pdf/`
  },
  downloadAppointmentConclusionSignedPdfBlob(appointmentId) {
    return apiClient.get(`/appointment-conclusions/${appointmentId}/signed-pdf/`, { responseType: 'blob' })
  },
  downloadPrescriptionPdfBlob(prescriptionId) {
    return apiClient.get(`/prescriptions/${prescriptionId}/pdf/`, { responseType: 'blob' })
  },
  suggestAppointmentConclusionTreatmentPlan(conclusionData) {
    return apiClient.post('/appointment-conclusions/ai/suggest-treatment-plan/', conclusionData)
  },
  getPreviousAppointmentConclusion(appointmentId, template) {
    return apiClient.get(`/appointment-conclusions/previous/${appointmentId}/`, { params: { template } })
  },
  getAppointmentConclusion(appointmentId, requestConfig = {}) {
    const existingParams = requestConfig.params || {}

    // Ищем только детальное заключение (AppointmentConclusion)
    return apiClient.get('/appointment-conclusions/', {
      ...requestConfig,
      params: {
        ...existingParams,
        appointment: appointmentId
      }
    }).then(response => {

      // Обрабатываем пагинированный ответ от Django REST Framework
      let conclusions = []
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Если ответ - простой массив
          conclusions = response.data
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // Если ответ - пагинированный объект с полем results
          conclusions = response.data.results
        }
      }

      if (conclusions.length > 0) {
        return { data: conclusions[0] }
      } else {
        throw new Error('Заключение еще не создано для данного приема')
      }
    }).catch(error => {
      // Проверяем, если это ошибка авторизации или доступа
      if (error.response && error.response.status === 403) {
        throw new Error('У вас нет доступа к этому заключению')
      } else if (error.response && error.response.status === 404) {
        throw new Error('Заключение не найдено')
      } else if (error.message.includes('Заключение еще не создано')) {
        throw error // Пробрасываем наше сообщение
      } else {
        throw new Error('Ошибка при загрузке заключения')
      }
    })
  },

  getMkbCodes(params = {}) {
    return apiClient.get('/mkb-codes/', { params })
  },

  // Prescriptions 078/u and medicine directory
  getMedicines(params = {}) {
    return apiClient.get('/medicines/', { params })
  },
  getMedicinePrescriptionSuggestions(medicineId) {
    return apiClient.get(`/medicines/${medicineId}/prescription-suggestions/`)
  },
  getPrescriptions(appointmentId) {
    return apiClient.get('/prescriptions/', {
      params: { appointment: appointmentId }
    })
  },
  getPrescriptionDefaults(appointmentId) {
    return apiClient.get('/prescriptions/defaults/', {
      params: { appointment: appointmentId }
    })
  },
  createPrescription(payload) {
    return apiClient.post('/prescriptions/', payload)
  },
  updatePrescription(prescriptionId, payload) {
    return apiClient.patch(`/prescriptions/${prescriptionId}/`, payload)
  },
  getPrescriptionSigningPolicy(appointmentId) {
    return apiClient.get('/prescriptions/signing-policy/', {
      params: { appointment: appointmentId }
    })
  },
  preparePrescriptionSign(payload) {
    return apiClient.post('/prescriptions/prepare-sign/', payload)
  },
  finalizePrescriptionSign(payload) {
    return apiClient.post('/prescriptions/finalize-sign/', payload)
  },
  deletePrescription(prescriptionId) {
    return apiClient.delete(`/prescriptions/${prescriptionId}/`)
  },
  getPrescriptionPdfUrl(prescriptionId) {
    return `${apiClient.defaults.baseURL}/prescriptions/${prescriptionId}/pdf/`
  },

  // Consilium endpoints
  getClinicDoctorsForConsilium() {
    return apiClient.get('/patient/appointments/clinic-doctors/')
  },

  inviteToConsilium(appointmentId, doctorIds) {
    return apiClient.post(`/patient/appointments/${appointmentId}/invite-to-consilium/`, {
      doctor_ids: doctorIds
    })
  },

  leaveConsilium(appointmentId) {
    return apiClient.post(`/patient/appointments/${appointmentId}/leave-consilium/`)
  },

  // FHIR Integration endpoints через бэкенд прокси (решение проблемы CORS)

  // Получение всех лабораторных данных пациента через бэкенд
  async getPatientLaboratoryData(patientId) {
    try {
      const response = await apiClient.get(`/fhir/patients/${patientId}/laboratory-data/`)
      return response.data
    } catch (error) {

      // Если ошибка от бэкенда, возвращаем структурированный ответ
      if (error.response?.data) {
        return error.response.data
      }

      return {
        hasAccess: false,
        reason: 'error',
        error: error.message,
        message: 'Ошибка при получении лабораторных данных'
      }
    }
  },

  // Получение DiagnosticReports по ServiceRequest ID
  async getDiagnosticReportsByServiceRequest(serviceRequestId, accessToken) {
    const response = await apiClient.get(`/fhir/service-requests/${serviceRequestId}/diagnostic-reports/`, {
      params: { token: accessToken }
    })
    return response.data
  },

  // Получение детального Observation через бэкенд
  async getFhirObservation(observationId, accessToken) {
    return apiClient.get(`/fhir/observations/${observationId}/`, {
      params: { token: accessToken }
    })
  },

  // Загрузка ECG waveform файла через прокси (для обхода CORS)
  // НОВЫЙ ПОДХОД: Использует POST вместо GET для передачи URL в теле запроса
  async downloadEcgWaveform(waveformUrl, accessToken) {
    const response = await apiClient.post('/fhir/ecg-waveform/', {
      url: waveformUrl,
      token: accessToken
    }, {
      responseType: 'text',
      timeout: 180000
    })
    return response.data
  },

  // Get FHIR laboratory statistics
  async getFhirLaboratoryStatistics(params = {}) {
    const response = await apiClient.get('/fhir/statistics/', {
      params,
      timeout: 180000
    })
    return response.data
  },

  // ========================================
  // SURVEYS API (Опросники)
  // ========================================

  // Staff endpoints - управление опросами
  getSurveys(params = {}) {
    return apiClient.get('/surveys/surveys/', { params })
  },
  getSurvey(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/`)
  },
  createSurvey(surveyData) {
    return apiClient.post('/surveys/surveys/', surveyData)
  },
  updateSurvey(surveyId, surveyData) {
    return apiClient.put(`/surveys/surveys/${surveyId}/`, surveyData)
  },
  deleteSurvey(surveyId) {
    return apiClient.delete(`/surveys/surveys/${surveyId}/`)
  },
  publishSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/publish/`)
  },
  unpublishSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/unpublish/`)
  },
  duplicateSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/duplicate/`)
  },

  // Вопросы опроса
  getSurveyQuestions(surveyId) {
    return apiClient.get('/surveys/questions/', { params: { survey: surveyId } })
  },
  createQuestion(questionData) {
    return apiClient.post('/surveys/questions/', questionData)
  },
  updateQuestion(questionId, questionData) {
    return apiClient.put(`/surveys/questions/${questionId}/`, questionData)
  },
  deleteQuestion(questionId) {
    return apiClient.delete(`/surveys/questions/${questionId}/`)
  },

  // Статистика опроса
  getSurveyStatistics(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/statistics/`)
  },

  // Ответы пациентов (для врачей)
  getSurveyResponses(params = {}) {
    return apiClient.get('/surveys/responses/', { params })
  },
  getSurveyResponse(responseId) {
    return apiClient.get(`/surveys/responses/${responseId}/`)
  },
  getPatientSurveyResponses(patientId) {
    return apiClient.get('/surveys/responses/', { params: { patient: patientId } })
  },

  // Mobile endpoints - для пациентов
  getAvailableSurveys(lang = 'ru') {
    return apiClient.get('/surveys/surveys/available/', { params: { lang } })
  },
  takeSurvey(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/take/`)
  },
  submitSurvey(surveyId, answers) {
    return apiClient.post(`/surveys/surveys/${surveyId}/submit/`, { answers })
  },

  // Психологический мониторинг
  getRiskDashboard(params = {}) {
    return apiClient.get('/surveys/risk-dashboard/', { params })
  },
  getRiskAssessment(responseId) {
    return apiClient.get(`/surveys/responses/${responseId}/risk_assessment/`)
  },
}
